import React, { useEffect, useRef, useState } from "react";
import { PartyPopper } from "lucide-react";

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const readVar = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch (e) {
    return fallback;
  }
};

/**
 * Micro-celebration: a short confetti burst (canvas, no deps) + a small toast.
 * Trigger by passing a new `burst` = { key, message }. Confetti is skipped when
 * the user prefers reduced motion; the toast still shows.
 */
const Celebration = ({ burst }) => {
  const canvasRef = useRef(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!burst || !burst.key) return undefined;
    setToast(burst.message || "Nice!");
    const hideId = setTimeout(() => setToast(null), 2600);

    if (reducedMotion() || !canvasRef.current) return () => clearTimeout(hideId);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return () => clearTimeout(hideId);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    const colors = [
      readVar("--brand-a", "#facc15"),
      readVar("--brand-b", "#22d3ee"),
      "#4ade80",
      "#f472b6",
      "#a78bfa",
    ];
    const N = 140;
    const parts = Array.from({ length: N }, (_, i) => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
      const speed = 7 + Math.random() * 9;
      return {
        x: W / 2 + (Math.random() - 0.5) * 120,
        y: H * 0.55,
        vx: Math.cos(angle) * speed * (i % 2 ? 1 : 0.8),
        vy: Math.sin(angle) * speed,
        w: 6 + Math.random() * 6,
        h: 4 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        color: colors[i % colors.length],
      };
    });
    const start = performance.now();
    const DURATION = 1500;
    let raf = 0;
    const tick = (now) => {
      const t = now - start;
      ctx.clearRect(0, 0, W, H);
      const fade = t > DURATION - 400 ? Math.max(0, (DURATION - t) / 400) : 1;
      for (const p of parts) {
        p.vy += 0.35;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (t < DURATION) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, W, H);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      clearTimeout(hideId);
      cancelAnimationFrame(raf);
      ctx.clearRect(0, 0, W, H);
    };
  }, [burst]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-[58] pointer-events-none"
        aria-hidden="true"
      />
      {toast && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-[57] glass rounded-2xl card-shadow px-4 py-2.5 flex items-center gap-2 text-sm font-semibold text-yellow-300 animate-fade-in"
          role="status"
          aria-live="polite"
        >
          <PartyPopper className="w-4 h-4" />
          {toast}
        </div>
      )}
    </>
  );
};

export default Celebration;
